import { Component, Output, EventEmitter, Input, HostListener, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';

import { ConstantsService } from '../../../services/constants.service';
import { Constant, LocationData } from '../../../datatypes/misc';
import { JobService } from '../../../services/job.service';
import { AdvertService } from '../../../services/advert.service';
import { IJobCategory, AdvertQueryResponse } from '../../../models/advert.model';
import { AdvertQuery, SearchTerm, SearchTermTypes } from '../../../models/advertQuery.model';
import { HelpersService } from '../../../helpers/helpers.service';
import { SearchFormSharedService } from '../../search/searchForm/searchForm.shared.service';
import { Constants } from '../../../datatypes/constants';
import { ILocationData } from '../../../datatypes/misc';
import { AppConfig } from '../../../app.config';
import { BooleanSubject } from '../../../datatypes/misc';
import { JobAlertService } from '../../account/manage/job-alerts/services/job-alert.service';
import { ActivatedRoute } from '@angular/router';
import { ECategoryCriteria } from '../../account/manage/job-alerts/enums/ECategoryCriteriaEnum';
import { IJobAlert } from '../../account/manage/job-alerts/models';
import { CorporateConfig } from '../../../corporate.config';
import { switchMap } from 'rxjs/operators';

@Component({
    selector: 'searchForm',
    templateUrl: './searchForm.component.html',
    styleUrls: ['./searchForm.component.css']
})
export class SearchFormComponent implements OnInit {

    @Output()
    public viewSearchResult = new EventEmitter<any>();

    @Input()
    public searchResponse = new AdvertQueryResponse();

    @Input()
    public resetSearchForm: BooleanSubject;

    // This array is for the template so we can loop through the result to view them in the criteria section
    searchResultTemplateArr = this.searchFormSharedService.getSearchTermValue();

    model: AdvertQuery;
    isNoLocation = true;
    disableDistanceSelection = false;
    isMobileView = false;
    isSearchContainerVisible = true;
    isSortVisible = false;
    isRefineVisible = false;
    advertSearchResults: AdvertQueryResponse;
    hours: Constant[];
    contractTypes: Constant[];
    jobCategories: IJobCategory[];
    proximityDistanceList: number[] = [];
    searchKeywordPlaceholder: string;

    readonly UseSearchAreaDistanceControl = AppConfig.UseSearchAreaDistanceControl;

    private lastWidth = window.innerWidth;
    private mobileBreakPoint = Constants.MobileScreenBreakingPoint;


    constructor(
        private constantsService: ConstantsService,
        private jobService: JobService,
        public searchFormSharedService: SearchFormSharedService,
        private advertService: AdvertService,
        public helpersService: HelpersService,
        private jobAlertService: JobAlertService,
        private route: ActivatedRoute
    ) {
        this.model = new AdvertQuery();
        this.contractTypes = [];
        this.jobCategories = [];
        this.hours = [];
    }

    ngOnInit(): void {
        if (this.route.snapshot.queryParamMap.has('JBEId')) {
            this.route.queryParamMap
                .pipe(
                    switchMap(params => this.jobAlertService.get(+params.get('JBEId')))
                ).subscribe(jobAlert => {
                    const advertQuery = this.setAdvertQuery(jobAlert);
                    this.setSearchTerms(advertQuery);
                    this.setModel();
                });
        } else {
            this.setModel();
        }


        this.searchKeywordPlaceholder = this.helpersService.getPageContent(CorporateConfig.searchKeywordPlaceholder);
        this.constantsService.getHours().subscribe(res => this.hours = res);
        this.constantsService.getJobTypes().subscribe(res => this.contractTypes = res);
        this.jobService.getJobCategories().subscribe(res => this.jobCategories = res);
        this.proximityDistanceList = this.helpersService.getProximityDistance();
        this.onResize();
        this.toggleViewSearchContainer();
        this.resetSearchForm.subscribe(event => this.resetForm(event));
    }

    doSearch() {
        this.resetLocationWhenNoAddressSelected();
        this.createSearchResultTemplateArr();

        this.advertService.getAdvertSearch(this.model).subscribe(r => this.setAdvertQueryResponse(r));
        this.toggleViewSearchContainer();
        this.toggleDistanceTab();
        this.toggleRelevanceTab();
        this.searchFormSharedService.setResetFilterOnNewSearch(true);
    }

    removeSearchTerm(form: NgForm, value: SearchTerm) {
        this.searchResultTemplateArr = this.searchResultTemplateArr.filter(item => item !== value);

        this.removeKeyword(value);
        this.removeLocationCoordinates(value);
        this.removeDistance(value);
        this.doSearch();
    }

    // remove the distance entry regardless of value & reset the search query model's distance value'
    removeDistanceByTerm() {
        const res = this.searchResultTemplateArr.filter(item => item.type === SearchTermTypes.DISTANCE);
        if (res) {
            const searchTermValue = res[0];
            this.searchResultTemplateArr = this.searchResultTemplateArr.filter(item => item !== searchTermValue);
            this.model.LocationPlaceData.DistanceInMiles = AppConfig.DefaultDistance;
        }
    }

    resetForm(event: boolean) {
        if (event) {
            this.searchResultTemplateArr = [];
            this.model = new AdvertQuery();
        }
    }

    setLocationData(result: ILocationData) {
        this.isNoLocation = result.LocationName === '';
        // if true, large areas selected by the user will disable the distance selector
        if (this.UseSearchAreaDistanceControl) {
            const conurbation = result.PlaceResultMetaData.IsLargeConurbation;
            if (conurbation) {
                this.disableDistanceSelection = conurbation;
                this.removeDistanceByTerm();
            } else {
                this.disableDistanceSelection = false;
            }
        }
    }

    isReverseLocationSearch(event: boolean) {
        if (event) {
            this.isNoLocation = false;
        }
    }

    // Toggle the tabs target divs on mobile screen
    toggleSearchContainer() {
        this.isSortVisible = false;
        this.isRefineVisible = false;
        if (this.isMobileView) {

            this.isSearchContainerVisible = !this.isSearchContainerVisible;
        } else {

            this.isSearchContainerVisible = true;
        }
    }

    toggleRefine() {
        this.isSortVisible = false;
        this.isSearchContainerVisible = false;
        if (this.isMobileView) {
            this.isRefineVisible = !this.isRefineVisible;
        } else {
            this.isRefineVisible = true;
        }

    }

    toggleSort() {
        this.isSearchContainerVisible = false;
        this.isRefineVisible = false;
        if (this.isMobileView) {
            this.isSortVisible = !this.isSortVisible;
        } else {
            this.isSortVisible = true;
        }
    }

    private setModel() {
        let shouldSearch = false;

        const homePageSearchQueries = this.searchFormSharedService.getData();

        if (homePageSearchQueries !== null && homePageSearchQueries !== undefined) {
            this.model = homePageSearchQueries;
            this.setLocationData(homePageSearchQueries.LocationPlaceData);

            shouldSearch = true;
            this.searchFormSharedService.resetSearchCriteria();
        }

        // We check if we search here to search using any home page search queries if there are any
        if (shouldSearch) {
            this.doSearch();
        }

        const searchTermValue = this.searchFormSharedService.getSearchTermValue();

        if (searchTermValue.filter(x => x.type === SearchTermTypes.KEYWORD).length > 0) {
            this.model.KeywordParams = searchTermValue.find(x => x.type === SearchTermTypes.KEYWORD).value;
            shouldSearch = true;
        }

        const locationDataValue = this.searchFormSharedService.getLocationDataValue();

        if (locationDataValue) {
            this.model.LocationPlaceData = locationDataValue;
            shouldSearch = true;
            this.isNoLocation = false;
        }

        this.model.PagingDictionary = this.searchFormSharedService.getPagingDictionaryData();

        this.model.AdvertSortOrder = this.searchFormSharedService.getSortOrderValue();

        this.doSearch();
    }

    private setAdvertQueryResponse(result: AdvertQueryResponse) {
        this.advertSearchResults = result;
        this.searchFormSharedService.setSearchResults(result);
        this.viewSearchResult.emit(this.advertSearchResults);
    }

    // TODO - refactor so that this is implemented by the model itself
    private createSearchResultTemplateArr() {

        this.searchResultTemplateArr = [];
        if (this.model.KeywordParams) {
            const obj = new SearchTerm(this.model.KeywordParams, SearchTermTypes.KEYWORD);
            this.searchResultTemplateArr.push(obj);
        }

        if (this.model.LocationPlaceData.Coordinates.Lat !== 0 && this.model.LocationPlaceData.Coordinates.Lng !== 0) {
            const obj = new SearchTerm(this.model.LocationPlaceData.LocationName, SearchTermTypes.LOCATION);
            this.searchResultTemplateArr.push(obj);
            this.searchFormSharedService.setLocationData(this.model.LocationPlaceData);
        }

        if (this.model.LocationPlaceData.DistanceInMiles >= 0 && !this.isNoLocation) {
            const obj = new SearchTerm(this.model.LocationPlaceData.DistanceInMiles, SearchTermTypes.DISTANCE);
            this.searchResultTemplateArr.push(obj);
        }

        this.searchFormSharedService.setSearchTerm(this.searchResultTemplateArr);
    }

    private removeKeyword(val: SearchTerm) {
        if (val.type === SearchTermTypes.KEYWORD) {
            this.model.KeywordParams = '';
            this.searchFormSharedService.setRelevanceTabView(false);
        }
    }

    private removeLocationCoordinates(val: SearchTerm) {
        if (val.type === SearchTermTypes.LOCATION) {
            this.model.LocationPlaceData = new LocationData();
            this.isNoLocation = true;
            this.searchFormSharedService.setDistanceTabView(false);
        }
    }

    private removeDistance(val: SearchTerm) {
        if (val.type === SearchTermTypes.DISTANCE) {
            this.model.LocationPlaceData.DistanceInMiles = 0;
        }
    }

    private toggleViewSearchContainer(): void {
        if (window.innerWidth < this.mobileBreakPoint) {
            this.isSearchContainerVisible = false;
        } else {
            this.isSearchContainerVisible = true;
        }
    }

    private isEmptyAdvertQueryResponse(obj: AdvertQueryResponse): boolean {
        return (obj && (Object.keys(obj).length === 0));
    }

    // When having location data set through autocomplete and search conducted then removed the location name,
    // this resets the saved location
    private resetLocationWhenNoAddressSelected(): void {
        if (this.model.LocationPlaceData.LocationName === '') {
            this.model.LocationPlaceData = new LocationData();
            this.model.LocationPlaceData.DistanceInMiles = AppConfig.DefaultDistance;
            this.isNoLocation = true;
        }
    }

    private toggleRelevanceTab() {
        this.searchFormSharedService.setRelevanceTabView(this.model.KeywordParams !== '');
    }

    private toggleDistanceTab() {
        this.searchFormSharedService.setDistanceTabView(this.model.LocationPlaceData.LocationName !== '');
    }

    private setAdvertQuery(jobAlert: IJobAlert) {
        const advertQuery = new AdvertQuery();

        const keywordCriteria = jobAlert.Criteria.find(x => x.Category === ECategoryCriteria.Keywords);
        if (keywordCriteria) {
            advertQuery.KeywordParams = keywordCriteria.Uri;
        }

        const locationCriteria = jobAlert.Criteria.find(x => x.Category === ECategoryCriteria.Location);
        if (locationCriteria) {
            advertQuery.LocationPlaceData = JSON.parse(locationCriteria.Uri);
        }

        return advertQuery;
    }

    private setSearchTerms(advertQuery: AdvertQuery) {
        const searchResultTemplateArr = [];

        const keyword = new SearchTerm(advertQuery.KeywordParams, SearchTermTypes.KEYWORD);
        const location = new SearchTerm(advertQuery.LocationPlaceData.LocationName, SearchTermTypes.LOCATION);
        const distance = new SearchTerm(advertQuery.LocationPlaceData.DistanceInMiles, SearchTermTypes.DISTANCE);

        searchResultTemplateArr.push(keyword, location, distance);

        this.searchFormSharedService.setLocationData(advertQuery.LocationPlaceData);
        this.searchFormSharedService.setSearchTerm(searchResultTemplateArr);
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        if (window.innerWidth < this.mobileBreakPoint) {
            this.isMobileView = true;
            if (this.lastWidth > this.mobileBreakPoint) {
                this.isSearchContainerVisible = false;
            }
        } else {
            this.isMobileView = false;
            if (this.lastWidth < this.mobileBreakPoint) {
                this.isSearchContainerVisible = true;
            }
        }
        this.lastWidth = window.innerWidth;
    }
}
